import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import { ProjectMentorRequest } from '../../types/content';
import Button from '../../components/UI/Button';
import { confirmAction } from '../../utils/confirmAction';
import { formatDate } from '../../utils/formatDate';

const ProjectMentorRequestsAdminPage: React.FC = () => {
  const [rows, setRows] = useState<ProjectMentorRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [closingId, setClosingId] = useState<number | null>(null);

  const load = async () => {
    try {
      const r = await contentApi.listProjectMentorRequests({ status: 'Open' });
      setRows(r.data.requests || []);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load mentor requests.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const projectOptions = useMemo(() => {
    const titles = [...new Set(rows.map((row) => row.projectTitle).filter(Boolean))];
    return titles.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(
    () => rows.filter((row) => !projectFilter || row.projectTitle === projectFilter),
    [rows, projectFilter]
  );

  const closeRequest = async (row: ProjectMentorRequest) => {
    const ok = await confirmAction({
      title: 'Assign a mentor?',
      description: `Mark the request from ${row.userEmail || `profile #${row.userProfileId}`} on “${row.projectTitle}” as assigned.`,
      confirmLabel: 'Mark assigned',
    });
    if (!ok) return;
    setClosingId(row.id);
    try {
      const response = await contentApi.closeProjectMentorRequest(row.id);
      if (!response.data.success) {
        setError(response.data.message || 'Could not close the request.');
        return;
      }
      setRows((prev) => prev.filter((item) => item.id !== row.id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not close the request.');
    } finally {
      setClosingId(null);
    }
  };

  if (error && rows.length === 0) return <p className="text-gray-600">{error}</p>;

  return (
    <div>
      <p className="text-gray-600 mb-4">
        Open requests from learners who want a project mentor. Owner and Course Admin can filter by project and mark a request as assigned.
      </p>
      {error && <p className="mb-3 text-sm text-error-600">{error}</p>}

      <div className="mb-4">
        <label className="text-sm text-gray-600">
          Project
          <select
            className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="">All projects</option>
            {projectOptions.map((title) => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
        </label>
      </div>

      {filtered.length === 0 ? (
        <p className="text-gray-500">No open mentor requests{projectFilter ? ' for this project' : ''}.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Requested</th>
                <th className="px-4 py-3 font-medium">Project</th>
                <th className="px-4 py-3 font-medium">Learner</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDate(row.dateCreated)}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{row.projectTitle}</td>
                  <td className="px-4 py-3 text-gray-700">{row.userEmail || `#${row.userProfileId}`}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="secondary"
                      isLoading={closingId === row.id}
                      onClick={() => void closeRequest(row)}
                    >
                      Mark assigned
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectMentorRequestsAdminPage;
